// Units for the splice save (SPEC §8): top-level blocks, list items (recursively),
// table rows (header + delimiter row as one unit). Each unit has a fingerprint: its
// meaning, independent of formatting. Raw HTML is fingerprinted by its exact bytes.
import type { List, ListItem, Nodes, Root, RootContent, Table, TableRow } from 'mdast';
import { toMarkdown, type Options as ToMarkdownOptions } from 'mdast-util-to-markdown';
import { gfmToMarkdown } from 'mdast-util-gfm';
import { endOf, startOf } from './parse';

export type UnitKind = 'block' | 'item' | 'table-head' | 'table-row' | 'table';

export type Unit = {
  kind: UnitKind;
  /** Source range [start, end) in the document the unit came from. */
  start: number;
  end: number;
  fp: string;
  /** Node to render: block, list item head (nested lists removed), row or table. */
  node: Nodes;
  /** List nesting depth for items (0 = top-level list). */
  depth: number;
  list?: { ordered: boolean; start: number; index: number; key: number };
  table?: Table;
};

function strip<T extends Nodes>(n: T): T {
  const { position: _p, ...rest } = n as T & { position?: unknown };
  const out = rest as T;
  if ('children' in out) (out as { children: Nodes[] }).children = (out as unknown as { children: Nodes[] }).children.map(strip);
  return out;
}

const CANON: ToMarkdownOptions = {
  bullet: '-', bulletOrdered: '.', emphasis: '_', strong: '*', fence: '`', rule: '-',
  listItemIndent: 'one', incrementListMarker: true, setext: false, closeAtx: false,
  extensions: [gfmToMarkdown({ tablePipeAlign: false, tableCellPadding: true })],
};

export function canonical(nodes: Nodes | Nodes[]): string {
  const children = (Array.isArray(nodes) ? nodes : [nodes]).map(strip) as RootContent[];
  return toMarkdown({ type: 'root', children } as Root, CANON).trim();
}

const cells = (row: TableRow) => row.children.map((c) => canonical({ type: 'paragraph', children: c.children })).join('\u0001');

/** Is every `|` in the table lined up (columns padded to equal width)? */
export function isAligned(md: string, t: Table): boolean {
  const lines = md.slice(startOf(t), endOf(t)).split('\n');
  if (lines.length < 2) return false;
  const cols = lines.map((l) => [...l].flatMap((ch, i) => (ch === '|' ? [i] : [])).join(','));
  return cols.every((c) => c === cols[0]) && lines.some((l) => /\s{2,}\|/.test(l));
}

/** `granular: false` = top-level blocks only (fallback when the fine-grained splice can't be verified). */
export function unitize(md: string, tree: Root, granular = true): Unit[] {
  const units: Unit[] = [];
  let listKey = 0;

  const items = (list: List, depth: number) => {
    const key = listKey++;
    list.children.forEach((li: ListItem, index) => {
      const k = li.children.findIndex((c) => c.type === 'list');
      const splittable = k > 0 && li.children.slice(k).every((c) => c.type === 'list');
      const head: ListItem = splittable ? { ...li, children: li.children.slice(0, k) } : li;
      const end = splittable ? endOf(li.children[k - 1]!) : endOf(li);
      const fpItem = strip({ ...head, spread: false });
      units.push({
        kind: 'item', start: startOf(li), end, node: head, depth,
        list: { ordered: !!list.ordered, start: list.start ?? 1, index, key },
        fp: `i:${depth}:${list.ordered ? 'o' : 'u'}:` + canonical({ type: 'list', ordered: !!list.ordered, start: 1, spread: false, children: [fpItem] }),
      });
      if (splittable) for (const nested of li.children.slice(k) as List[]) items(nested, depth + 1);
    });
  };

  for (const node of tree.children) {
    if (granular && node.type === 'list') { items(node, 0); continue; }
    if (granular && node.type === 'table' && node.children.length > 0) {
      // Always split, whatever the formatting: unit boundaries must not depend on style.
      // (Aligned tables are re-rendered as a whole by the splice when a row changes.)
      const [head, ...rows] = node.children;
      const afterHead = md.indexOf('\n', endOf(head!) + 1); // end of the delimiter row
      const headEnd = afterHead === -1 || afterHead > endOf(node) ? endOf(node) : afterHead;
      units.push({ kind: 'table-head', start: startOf(node), end: headEnd, node: head!, table: node, depth: 0, fp: `th:${node.align?.join(',')}:${cells(head!)}` });
      for (const r of rows) units.push({ kind: 'table-row', start: startOf(r), end: endOf(r), node: r, table: node, depth: 0, fp: `tr:${cells(r)}` });
      continue;
    }
    const fp = node.type === 'html' ? `h:${node.value}` : `b:${canonical(node)}`;
    units.push({ kind: 'block', start: startOf(node), end: endOf(node), node, depth: 0, fp });
  }
  return units;
}
